<template>
  <application-layout>
    <v-container fluid>
      <v-stepper v-model="stepperStep" style="height: 87vh">
        <v-stepper-header>
          <v-stepper-step
            :color="stepperStep === 1 ? 'secondary' : 'success'"
            :complete="stepperStep > 1"
            step="1"
            :class="stepperStep === 1 ? 'title' : 'subheading'"
          >
            Unit Select
          </v-stepper-step>
          <v-divider :class="stepperStep > 1 ? 'success' : null"></v-divider>
          <v-stepper-step
            :color="stepperStep === 2 ? 'secondary' : 'success'"
            :complete="stepperStep > 2"
            step="2"
            :class="stepperStep === 2 ? 'title' : 'subheading'"
          >
            Payment Review
          </v-stepper-step>
          <v-divider :class="stepperStep > 2 ? 'success' : null"></v-divider>
          <v-stepper-step
            :color="stepperStep === 3 ? 'secondary' : 'success'"
            :complete="stepperStep === 3"
            step="3"
            :class="stepperStep === 3 ? 'title' : 'subheading'"
          >
            Payment Invoice
          </v-stepper-step>
        </v-stepper-header>
          <v-stepper-items :class="darkMode ? 'secondary' : 'grey lighten-4'">
              <v-layout column style="height: 70.25vh; overflow: auto">
                  <v-layout>
                      <v-flex :xs8="stepperStep < 3">
                          <v-stepper-content step="1">
                              <plan-unit-select />
                          </v-stepper-content>
                          <v-stepper-content step="2">
                              <plan-review />
                          </v-stepper-content>
                          <v-stepper-content step="3">
                              <plan-invoice />
                          </v-stepper-content>
                      </v-flex>
                      <v-flex xs4 v-if="stepperStep < 3">
                          <v-container data-testid="side-column">
                              <plan-side-total />
                              <div class="px-3 py-4">
                                  <div class="text-h6">Plan Period:</div>
                                  <v-radio-group v-model="planMonths" row>
                                      <v-radio
                                          data-testid="payment-two-months-radio"
                                          class="payment-two-months-radio"
                                          label="Two Months"
                                          color="primary"
                                          value="2"
                                      />
                                      <v-radio
                                          data-testid="payment-three-months-radio"
                                          class="payment-three-months-radio"
                                          label="Three Months"
                                          color="info"
                                          value="3"
                                      />
                                  </v-radio-group>
                              </div>
                          </v-container>
                      </v-flex>
                  </v-layout>
              </v-layout>
              <v-divider />
              <v-flex>
                  <stepper-nav-buttons
                      :loading="loading"
                      @backToTenant="handleBackToTenant"
                      @nextStep="handleNextStep"
                      @openDisposition="handleDisposition"
                      @priorStep="handlePriorStep"
                      @submit="handleCreatePaymentPlan"
                  />
              </v-flex>
          </v-stepper-items>
      </v-stepper>
      <disposition
        :dispositionOpen="dispositionOpen"
        @collapse="dispositionOpen = false"
        isAccountsReceivable
      />
    </v-container>
  </application-layout>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import ApplicationLayout from "@/layouts/ApplicationLayout";
import Disposition from "@/components/Disposition";
import PlanInvoice from "@/modules/accounts-receivable/views/payment-plan/steps/PlanInvoice";
import PlanReview from "@/modules/accounts-receivable/views/payment-plan/steps/PlanReview";
import PlanSideTotal from "@/modules/accounts-receivable/components/PlanSideTotal";
import PlanUnitSelect from "@/modules/accounts-receivable/views/payment-plan/steps/PlanUnitSelect";
import StepperNavButtons from "@/modules/accounts-receivable/components/StepperNavButtons";
import router from "@/router";

export default {
  name: "ARPayment",

  components: {
    ApplicationLayout,
    Disposition,
    StepperNavButtons,
    PlanSideTotal,
    PlanInvoice,
    PlanReview,
    PlanUnitSelect
  },

  data() {
    return {
      dispositionOpen: false,
      loading: false
    };
  },

  methods: {
    async handleBackToTenant() {
      await this.$store.commit("setCurrentStep", 1);
      await this.$store.commit("resetAccountsReceivableState");
      await this.getAllPaymentPlans();
      await this.getAllPromiseToPays();
      await router.push({
        name: "arTenant",
        params: {
          facilityId: this.$route.params.facilityId,
          tenantId: this.$route.params.tenantId
        }
      }).catch(() => {});
    },

    handleNextStep() {
      this.$store.commit("setCurrentStep", (this.currentStep + 1));
    },

    handlePriorStep() {
      this.$store.commit("setCurrentStep", (this.currentStep - 1));
    },

    handleDisposition() {
      this.dispositionOpen = true;
    },

    handleCreatePaymentPlan() {
      this.loading = true;
      let payload = {
        start_date: this.planDate,
        duration: this.duration,
        total_balance: this.planCart.total_amount_due,
        monthly_rate: this.selectedPaymentPlan.total_due.toFixed(2),
        status: "pending",
        tenant_id: this.info.external_id,
        ledger_id: this.planCart.pms_ledger_id,
        facility_id: this.$route.params.facilityId,
        unit_id: this.planCart.facility_unit.sitelink_id, // @todo should be external_id
        first_name: this.info.first_name,
        last_name: this.info.last_name,
        email: this.info.email,
        phone: this.info.phone_number
      };

      this.$store.dispatch("createPaymentPlan", payload)
        .then(() => {
          this.$store.dispatch("handleSuccess", "Payment Plan successfully created!");
          this.handleNextStep();
        })
        .catch(err => {
          this.loading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    getAllPaymentPlans() {
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("getAllPaymentPlansForTenant", payload)
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getAllPromiseToPays() {
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      };

      this.$store.dispatch("getAllPromisesToPayForTenant", payload)
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    }
  },

  computed: {
    ...mapState({
      currentStep: state => state.application.currentStep,
      darkMode: state => state.application.settings.darkTheme,
      info: state => state.tenant.info,
      planCart: state => state.accountsReceivable.planCart,
      planDate: state => state.accountsReceivable.planDate,
      duration: state => state.accountsReceivable.planMonths
    }),

    ...mapGetters({
      selectedPaymentPlan: "selectedPaymentPlan"
    }),

    stepperStep: {
      get() {
        return this.currentStep;
      },
      set(newVal) {
        this.$store.commit("setCurrentStep", newVal);
      }
    },

    planMonths: {
      get() {
        return this.duration;
      },
      set(months) {
        this.$store.commit("setPlanMonths", months);
      }
    },

    tenantInfoExists() {
      return !!Object.values(this.info).filter(value => !!value).length;
    }
  },

  created() {
    if (!this.tenantInfoExists) {
      let payload = {
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      };
      this.$store.dispatch("getTenantInfo", payload)
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    }
  }
};
</script>
